import { Container, Input } from "components/atoms";
import StatisticsListContainer from "components/molecules/statistics/StatisticsListContainer";
import { useCustomParamId } from "hooks/useParamId";
import { usePermissionGuard } from "hooks/usePermissionGuard";
import { t } from "i18next";

import { PiVinylRecord, PiChartBar } from "react-icons/pi";
import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatCurrency } from "utils/global/currency.utils";
import { getSettlementPeriodName } from "utils/global/name.utils";
import { useGetSettlementLightList } from "services/settlementPeriods";
import {
  useGetMusicProductDigitalTopSalesChannelsForProduct,
  useGetMusicProductDigitalEarningsBySalesChannel,
  useGetMusicProductDigitalEarnings,
} from "services/statistics/musicProductDigital";
import { IStatisticsQuery } from "services/statistics/types";

const MusicProductDigitalStatistics = () => {
  usePermissionGuard("StatisticsPermissionsLIST**");

  const productId = useCustomParamId("id", "/music-product-digital");
  const settlementPeriod = useGetSettlementLightList();

  const [statisticsQuery, setStatisticsQuery] = useState<IStatisticsQuery>({
    startSettlementPeriodId: "",
    endSettlementPeriodId: "",
  });
  const [chartStatisticsQuery, setChartStatisticsQuery] = useState<IStatisticsQuery>({
    startSettlementPeriodId: "",
    endSettlementPeriodId: "",
  });
  const [selectedSalesChannelId, setSelectedSalesChannelId] =
    useState<string>("");

  const topSalesChannels = useGetMusicProductDigitalTopSalesChannelsForProduct(
    productId,
    statisticsQuery.startSettlementPeriodId,
    statisticsQuery.endSettlementPeriodId,
    !!statisticsQuery.startSettlementPeriodId &&
      !!statisticsQuery.endSettlementPeriodId
  );

  const earningsBySalesChannel =
    useGetMusicProductDigitalEarningsBySalesChannel(
      productId,
      selectedSalesChannelId,
      chartStatisticsQuery.startSettlementPeriodId,
      chartStatisticsQuery.endSettlementPeriodId,
      !!selectedSalesChannelId &&
        !!chartStatisticsQuery.startSettlementPeriodId &&
        !!chartStatisticsQuery.endSettlementPeriodId
    );

  const productEarnings = useGetMusicProductDigitalEarnings(
    productId,
    statisticsQuery.startSettlementPeriodId,
    statisticsQuery.endSettlementPeriodId,
    !!statisticsQuery.startSettlementPeriodId &&
      !!statisticsQuery.endSettlementPeriodId
  );

  const options = useMemo(
    () =>
      settlementPeriod.data?.map((p) => ({
        label:
          getSettlementPeriodName(p.settlementPeriodName) ||
          String(p.settlementPeriodName),
        value: p.id,
      })) ?? [],
    [settlementPeriod.data]
  );

  useEffect(() => {
    if (
      settlementPeriod.data &&
      !statisticsQuery.startSettlementPeriodId &&
      !statisticsQuery.endSettlementPeriodId
    ) {
      const end = settlementPeriod.data[0];
      const start = settlementPeriod.data[settlementPeriod.data.length - 1];

      setStatisticsQuery({
        endSettlementPeriodId: end.id,
        startSettlementPeriodId: start.id,
      });
    }
  }, [
    settlementPeriod.data,
    statisticsQuery.startSettlementPeriodId,
    statisticsQuery.endSettlementPeriodId,
  ]);

  useEffect(() => {
    if (
      settlementPeriod.data &&
      !chartStatisticsQuery.startSettlementPeriodId &&
      !chartStatisticsQuery.endSettlementPeriodId
    ) {
      const end = settlementPeriod.data[0];
      const start = settlementPeriod.data[settlementPeriod.data.length - 1];

      setChartStatisticsQuery({
        endSettlementPeriodId: end.id,
        startSettlementPeriodId: start.id,
      });
    }
  }, [
    settlementPeriod.data,
    chartStatisticsQuery.startSettlementPeriodId,
    chartStatisticsQuery.endSettlementPeriodId,
  ]);

  useEffect(() => {
    if (
      topSalesChannels.data &&
      topSalesChannels.data.length > 0 &&
      !selectedSalesChannelId
    ) {
      setSelectedSalesChannelId(topSalesChannels.data[0].id);
    }
  }, [topSalesChannels.data, selectedSalesChannelId]);

  const mappedTopSalesChannels = useMemo(() => {
    return (
      topSalesChannels.data
        ?.map((item) => ({
          name: item.name,
          total: item.allAmount,
        }))
        .sort((a, b) => b.total - a.total) ?? []
    );
  }, [topSalesChannels.data]);

  const chartData = useMemo(() => {
    return (
      earningsBySalesChannel.data
        ?.map((earning) => ({
          name: `${getSettlementPeriodName({ name: earning.settlementPeriod.name, year: earning.settlementPeriod.year })}`,
          value: earning.earnedAmount,
        }))
        .reverse() ?? []
    );
  }, [earningsBySalesChannel.data]);

  const totalProductEarnings = useMemo<number>(
    () =>
      productEarnings.data?.reduce(
        (acc, earning) => acc + earning.earnedAmount,
        0
      ) ?? 0,
    [productEarnings.data]
  );

  const mappedProductEarnings = useMemo(() => {
    return (
      productEarnings.data?.map((e) => ({
        name: getSettlementPeriodName(e.settlementPeriod) ?? "",
        value: e.earnedAmount ?? 0,
      })) ?? []
    );
  }, [productEarnings.data]);

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex gap-4 w-full">
        <Container className="max-w-full">
          <Container.Header
            title={
              <>
                <PiVinylRecord /> {t("statistics.product-earnings")}
              </>
            }
          >
            <div className="info-box-items flex p-4 w-full min-h-0 max-w-96">
              <Input.ComplexWrapper className="w-full">
                <Input.Select
                  label={t("input.label.start")}
                  value={
                    options.find(
                      (opt) =>
                        opt.value === statisticsQuery.startSettlementPeriodId
                    ) || undefined
                  }
                  onChange={(option) =>
                    setStatisticsQuery((prev) => ({
                      ...prev,
                      startSettlementPeriodId: option?.value as string,
                    }))
                  }
                  placeholder=""
                  options={options}
                  isClearable={false}
                />
                <Input.Select
                  label={t("input.label.end")}
                  value={
                    options.find(
                      (opt) =>
                        opt.value === statisticsQuery.endSettlementPeriodId
                    ) || undefined
                  }
                  onChange={(option) =>
                    setStatisticsQuery((prev) => ({
                      ...prev,
                      endSettlementPeriodId: option?.value as string,
                    }))
                  }
                  placeholder=""
                  options={options}
                  isClearable={false}
                />
              </Input.ComplexWrapper>
            </div>
          </Container.Header>
          <Container.Content loading={productEarnings.isLoading}>
            <div className="flex flex-col px-[150px] py-36 gap-2">
              <p className="opacity-50">{t("table.total")} </p>
              <p
                className=" font-bold text-xxl"
                style={{ color: "var(--primary)" }}
              >
                {formatCurrency(totalProductEarnings)}
              </p>
            </div>
            <ResponsiveContainer width={"100%"} height={350}>
              <BarChart
                data={mappedProductEarnings}
                margin={{
                  top: 0,
                  right: 100,
                  bottom: 20,
                }}
                barGap={2}
                barCategoryGap={10}
                maxBarSize={60}
              >
                <CartesianGrid
                  strokeDasharray=""
                  vertical={false}
                  opacity={0.5}
                />
                <XAxis dataKey="name" stroke="var(--secondary)" />
                <YAxis
                  width={150}
                  stroke="var(--secondary)"
                  tickFormatter={(value: any, index: number) =>
                    value === 0 ? "" : formatCurrency(parseFloat(`${value}`))
                  }
                  tickCount={5}
                />
                <Tooltip
                  cursor={{ fill: "none" }}
                  content={(props) => (
                    <div
                      className="flex  bg-opacity-95 rounded-sm p-8 gap-3"
                      style={{ backgroundColor: "var(--background)" }}
                    >
                      <p>{`${props.label}`}</p>
                      {props.payload && props.payload[0] && (
                        <>
                          <p>-</p>
                          <p style={{ color: props.payload[0].color }}>
                            {formatCurrency(
                              parseFloat(`${props.payload[0].value}`)
                            )}
                          </p>
                        </>
                      )}
                    </div>
                  )}
                />
                <ReferenceLine y={0} stroke="var(--secondary)" />
                <Brush dataKey="name" height={20} stroke="var(--primary)" />
                <Bar
                  dataKey="value"
                  fill="var(--primary)"
                  background={{ fill: "var(--primary)", opacity: 0.2 }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Container.Content>
        </Container>
      </div>

      <div className="flex gap-4 w-full">
                 <Container className="">
           <Container.Header
             title={
               <div className=" flex w-full justify-between">
                 <div className="flex gap-2">
                   {selectedSalesChannelId && topSalesChannels.data ? (
                     <>
                       <PiChartBar /> {t("statistics.earnings")} -{" "}
                       {topSalesChannels.data.find(
                         (ch) => ch.id === selectedSalesChannelId
                       )?.name || ""}
                     </>
                   ) : (
                     t("statistics.name")
                   )}
                 </div>
               </div>
             }
           >
             <div className="info-box-items flex p-4 w-full min-h-0 max-w-96">
               <Input.ComplexWrapper className="w-full">
                 <Input.Select
                   label={t("input.label.start")}
                   value={
                     options.find(
                       (opt) =>
                         opt.value === chartStatisticsQuery.startSettlementPeriodId
                     ) || undefined
                   }
                   onChange={(option) =>
                     setChartStatisticsQuery((prev) => ({
                       ...prev,
                       startSettlementPeriodId: option?.value as string,
                     }))
                   }
                   placeholder=""
                   options={options}
                   isClearable={false}
                 />
                 <Input.Select
                   label={t("input.label.end")}
                   value={
                     options.find(
                       (opt) =>
                         opt.value === chartStatisticsQuery.endSettlementPeriodId
                     ) || undefined
                   }
                   onChange={(option) =>
                     setChartStatisticsQuery((prev) => ({
                       ...prev,
                       endSettlementPeriodId: option?.value as string,
                     }))
                   }
                   placeholder=""
                   options={options}
                   isClearable={false}
                 />
               </Input.ComplexWrapper>
             </div>
           </Container.Header>
          <Container.Content>
            <ResponsiveContainer width={"100%"} height={370}>
              <LineChart
                data={chartData}
                margin={{
                  top: 20,
                  right: 100,
                  bottom: 20,
                }}
              >
                <CartesianGrid
                  strokeDasharray=""
                  vertical={false}
                  opacity={0.5}
                />
                <XAxis dataKey="name" stroke="var(--secondary-light)" />
                <YAxis
                  width={150}
                  stroke="var(--secondary-light)"
                  tickFormatter={(value: any, index: number) =>
                    value === 0 ? "" : formatCurrency(parseFloat(`${value}`))
                  }
                  tickCount={5}
                />
                <Tooltip
                  cursor={{ fill: "none" }}
                  content={(props) => (
                    <div
                      className="flex bg-opacity-95 rounded-sm p-8 gap-3"
                      style={{ backgroundColor: "var(--background)" }}
                    >
                      <p>{`${props.label}`}</p>
                      {props.payload && props.payload[0] && (
                        <>
                          <p>-</p>
                          <p style={{ color: props.payload[0].color }}>
                            {formatCurrency(
                              parseFloat(`${props.payload[0].value}`)
                            )}
                          </p>
                        </>
                      )}
                    </div>
                  )}
                />
                <ReferenceLine y={0} stroke="var(--secondary-light)" />
                <Line
                  type="monotone"
                  dataKey="value"
                  fill="var(--primary)"
                  stroke="var(--primary)"
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Container.Content>
        </Container>
                 <StatisticsListContainer
           classname="w-[40%] max-h-[441px] max-w-lg"
           headerTitle={
             <>
               <PiVinylRecord /> {t("statistics.top-sales-channels")}
             </>
           }
          label={(item) => item.name}
          value={(item) => formatCurrency(item.total)}
          items={mappedTopSalesChannels}
          loading={topSalesChannels.isLoading}
          isHighlighted={(item) => {
            const salesChannel = topSalesChannels.data?.find(
              (ch) => ch.name === item.name
            );
            return salesChannel?.id === selectedSalesChannelId;
          }}
          onItemClick={(item) => {
            const salesChannel = topSalesChannels.data?.find(
              (ch) => ch.name === item.name
            );
            if (salesChannel) {
              setSelectedSalesChannelId(salesChannel.id);
            }
          }}
          headerChildren={
            <div className="info-box-items flex p-4 w-full min-h-0 max-w-96">
              <Input.ComplexWrapper className="w-full">
                <Input.Select
                  label={t("input.label.start")}
                  value={
                    options.find(
                      (opt) =>
                        opt.value === statisticsQuery.startSettlementPeriodId
                    ) || undefined
                  }
                  onChange={(option) =>
                    setStatisticsQuery((prev) => ({
                      ...prev,
                      startSettlementPeriodId: option?.value as string,
                    }))
                  }
                  placeholder=""
                  options={options}
                  isClearable={false}
                />
                <Input.Select
                  label={t("input.label.end")}
                  value={
                    options.find(
                      (opt) =>
                        opt.value === statisticsQuery.endSettlementPeriodId
                    ) || undefined
                  }
                  onChange={(option) =>
                    setStatisticsQuery((prev) => ({
                      ...prev,
                      endSettlementPeriodId: option?.value as string,
                    }))
                  }
                  placeholder=""
                  options={options}
                  isClearable={false}
                />
              </Input.ComplexWrapper>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default MusicProductDigitalStatistics;
